/**
 * SKOP Community Reports
 *
 * Allows users to report scam/rug tokens with community aggregation.
 */

export type ReportReason = 'rug_pull' | 'honeypot' | 'fake_team' | 'scam' | 'other';
export type ReportStatus = 'pending' | 'verified' | 'dismissed';

export interface TokenReport {
  id: string;
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  reason: ReportReason;
  description?: string;
  evidenceLinks?: string[];
  reporterWallet: string;
  timestamp: number;
  status: ReportStatus;
  verifiedAt?: number;
}

export interface TokenReportSummary {
  tokenAddress: string;
  totalReports: number;
  verifiedReports: number;
  reasons: Record<ReportReason, number>;
  latestReportAt: number;
}

// Storage keys
const MY_REPORTS_KEY = 'skop_my_reports';
const TOKEN_REPORTS_KEY = 'skop_token_reports';
const REPORTED_TOKENS_KEY = 'skop_reported_tokens';

// ============================================================================
// Report Management
// ============================================================================

/**
 * Submit a new report for a token
 */
export function submitReport(
  walletAddress: string,
  report: Omit<TokenReport, 'id' | 'timestamp' | 'status' | 'reporterWallet'>
): TokenReport {
  const newReport: TokenReport = {
    ...report,
    id: generateReportId(),
    reporterWallet: walletAddress,
    timestamp: Date.now(),
    status: 'pending',
  };

  // Save to user's reports
  const myReports = getMyReports(walletAddress);
  myReports.push(newReport);
  saveMyReports(walletAddress, myReports);

  // Save to token's reports (simulated aggregation for MVP)
  const tokenReports = getTokenReports(report.tokenAddress);
  tokenReports.push(newReport);
  saveTokenReports(report.tokenAddress, tokenReports);

  // Update reported tokens index
  addToReportedTokensIndex(report.tokenAddress);

  return newReport;
}

/**
 * Get user's submitted reports
 */
export function getMyReports(walletAddress: string): TokenReport[] {
  try {
    const stored = localStorage.getItem(`${MY_REPORTS_KEY}_${walletAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save user's reports
 */
function saveMyReports(walletAddress: string, reports: TokenReport[]): void {
  localStorage.setItem(`${MY_REPORTS_KEY}_${walletAddress}`, JSON.stringify(reports));
}

/**
 * Get all reports for a token
 */
export function getTokenReports(tokenAddress: string): TokenReport[] {
  try {
    const stored = localStorage.getItem(`${TOKEN_REPORTS_KEY}_${tokenAddress}`);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save token reports
 */
function saveTokenReports(tokenAddress: string, reports: TokenReport[]): void {
  localStorage.setItem(`${TOKEN_REPORTS_KEY}_${tokenAddress}`, JSON.stringify(reports));
}

/**
 * Get report summary for a token
 */
export function getTokenReportSummary(tokenAddress: string): TokenReportSummary | null {
  const reports = getTokenReports(tokenAddress);
  if (reports.length === 0) return null;

  const reasons: Record<ReportReason, number> = {
    rug_pull: 0,
    honeypot: 0,
    fake_team: 0,
    scam: 0,
    other: 0,
  };

  let verifiedReports = 0;
  let latestReportAt = 0;

  for (const report of reports) {
    reasons[report.reason]++;
    if (report.status === 'verified') verifiedReports++;
    if (report.timestamp > latestReportAt) latestReportAt = report.timestamp;
  }

  return {
    tokenAddress,
    totalReports: reports.length,
    verifiedReports,
    reasons,
    latestReportAt,
  };
}

/**
 * Check if user has already reported a token
 */
export function hasUserReported(walletAddress: string, tokenAddress: string): boolean {
  const myReports = getMyReports(walletAddress);
  return myReports.some(r => r.tokenAddress === tokenAddress);
}

/**
 * Get list of tokens with reports
 */
export function getReportedTokens(): string[] {
  try {
    const stored = localStorage.getItem(REPORTED_TOKENS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Add token to reported tokens index
 */
function addToReportedTokensIndex(tokenAddress: string): void {
  const tokens = getReportedTokens();
  if (!tokens.includes(tokenAddress)) {
    tokens.push(tokenAddress);
    localStorage.setItem(REPORTED_TOKENS_KEY, JSON.stringify(tokens));
  }
}

// ============================================================================
// Mock Verification (for demo/MVP)
// ============================================================================

/**
 * Simulate verifying a report (in production, this would be admin action)
 */
export function verifyReport(tokenAddress: string, reportId: string): void {
  const reports = getTokenReports(tokenAddress);
  const report = reports.find(r => r.id === reportId);
  if (report) {
    report.status = 'verified';
    report.verifiedAt = Date.now();
    saveTokenReports(tokenAddress, reports);
  }
}

/**
 * Generate mock reports for a token (for demo purposes)
 */
export function generateMockReports(tokenAddress: string, tokenSymbol: string, tokenName: string): void {
  const existingReports = getTokenReports(tokenAddress);
  if (existingReports.length > 0) return; // Already has reports

  // Random chance of having reports
  if (Math.random() > 0.3) return; // 70% chance of no reports

  const reportCount = Math.floor(Math.random() * 8) + 1; // 1-8 reports
  const reasons: ReportReason[] = ['rug_pull', 'honeypot', 'fake_team', 'scam', 'other'];
  const mockWallets = [
    'Abc123...xyz',
    'Def456...uvw',
    'Ghi789...rst',
    'Jkl012...opq',
    'Mno345...lmn',
  ];

  const reports: TokenReport[] = [];
  for (let i = 0; i < reportCount; i++) {
    const isVerified = Math.random() > 0.7; // 30% verified
    reports.push({
      id: generateReportId(),
      tokenAddress,
      tokenSymbol,
      tokenName,
      reason: reasons[Math.floor(Math.random() * reasons.length)],
      description: Math.random() > 0.5 ? 'Suspicious activity detected' : undefined,
      evidenceLinks: Math.random() > 0.7 ? ['https://solscan.io/tx/example'] : undefined,
      reporterWallet: mockWallets[Math.floor(Math.random() * mockWallets.length)],
      timestamp: Date.now() - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000), // Within last week
      status: isVerified ? 'verified' : 'pending',
      verifiedAt: isVerified ? Date.now() - Math.floor(Math.random() * 3 * 24 * 60 * 60 * 1000) : undefined,
    });
  }

  saveTokenReports(tokenAddress, reports);
  addToReportedTokensIndex(tokenAddress);
}

// ============================================================================
// Risk Score Integration
// ============================================================================

/**
 * Calculate risk score boost from community reports
 * +5 per verified report, +1 per unverified (up to 10)
 * Max boost: 25 points
 */
export function calculateReportRiskBoost(tokenAddress: string): number {
  const summary = getTokenReportSummary(tokenAddress);
  if (!summary) return 0;

  let boost = 0;

  // Verified reports: +5 each
  boost += summary.verifiedReports * 5;

  // Unverified reports: +1 each, max 10
  const unverified = summary.totalReports - summary.verifiedReports;
  boost += Math.min(unverified, 10);

  // Cap at 25
  return Math.min(boost, 25);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate unique report ID
 */
function generateReportId(): string {
  return `report_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get reason label
 */
export function getReasonLabel(reason: ReportReason): string {
  switch (reason) {
    case 'rug_pull': return 'Rug Pull';
    case 'honeypot': return 'Honeypot';
    case 'fake_team': return 'Fake Team';
    case 'scam': return 'Scam';
    case 'other': return 'Other';
  }
}

/**
 * Get reason color
 */
export function getReasonColor(reason: ReportReason): string {
  switch (reason) {
    case 'rug_pull': return '#ff3b3b';
    case 'honeypot': return '#ff8800';
    case 'fake_team': return '#ffb800';
    case 'scam': return '#ff5555';
    case 'other': return '#888888';
  }
}

/**
 * Format relative time
 */
export function formatReportTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(timestamp).toLocaleDateString();
}
